// AppellationExplorePage — dedicated public page for a single appellation,
// rendered as a blog-style long-form editorial fed by the public Atlas blocks
// (already translated + polling handled by AtlasBlocksSection).
//
// Route: /explore/:slug
// Data: /api/public/appellations (for header meta) + AtlasBlocksSection
// (fetches /api/public/atlas-blocks/appellation/{slug}?lang=X).
// i18n : dictionnaire inline COPY (FR/EN/ES/PT).
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Map as MapIcon } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import AtlasBlocksSection from "@/components/AtlasBlocksSection";
import ReadingProgressBar from "@/components/ReadingProgressBar";
import { useLang } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const COPY = {
  fr: {
    home: "Accueil", explorer: "Explorer",
    seo_desc: (name, loc) => `Découvrez ${name}, appellation de ${loc}. Terroir, cépages, histoire, accords mets et vins.`,
    back_all: "Toutes les appellations",
    label_appellation: "Appellation",
    open_in_app: "Ouvrir dans l'app", see_map: "Voir la carte",
    is_appellation: "est une appellation viticole",
    located_in_region: "située dans la région",
    in_country: "en",
    sub_region: "sous-région",
    main_grapes: "Cépages principaux",
    go_further: "Aller plus loin",
    map_of: (name) => `Carte ${name}`,
    learn_on_app: (name) => `Apprendre ${name} sur geoVINUM app`,
    ask_ai: "Poser une question à geoVINUM AI",
    all_appellations_of: (region) => `Toutes les appellations de ${region}`,
  },
  en: {
    home: "Home", explorer: "Explorer",
    seo_desc: (name, loc) => `Discover ${name}, an appellation in ${loc}. Terroir, grapes, history, food pairings.`,
    back_all: "All appellations",
    label_appellation: "Appellation",
    open_in_app: "Open in the app", see_map: "See the map",
    is_appellation: "is a wine appellation",
    located_in_region: "located in the region of",
    in_country: "in",
    sub_region: "subregion",
    main_grapes: "Main grape varieties",
    go_further: "Go further",
    map_of: (name) => `${name} map`,
    learn_on_app: (name) => `Learn ${name} on geoVINUM app`,
    ask_ai: "Ask geoVINUM AI",
    all_appellations_of: (region) => `All appellations of ${region}`,
  },
  es: {
    home: "Inicio", explorer: "Explorer",
    seo_desc: (name, loc) => `Descubre ${name}, denominación de ${loc}. Terruño, variedades, historia, maridajes.`,
    back_all: "Todas las denominaciones",
    label_appellation: "Denominación",
    open_in_app: "Abrir en la app", see_map: "Ver el mapa",
    is_appellation: "es una denominación vitivinícola",
    located_in_region: "situada en la región de",
    in_country: "en",
    sub_region: "subregión",
    main_grapes: "Variedades principales",
    go_further: "Ir más lejos",
    map_of: (name) => `Mapa de ${name}`,
    learn_on_app: (name) => `Aprender ${name} en la app geoVINUM`,
    ask_ai: "Preguntar a geoVINUM AI",
    all_appellations_of: (region) => `Todas las denominaciones de ${region}`,
  },
  pt: {
    home: "Início", explorer: "Explorer",
    seo_desc: (name, loc) => `Descubra ${name}, denominação de ${loc}. Terroir, castas, história, harmonizações.`,
    back_all: "Todas as denominações",
    label_appellation: "Denominação",
    open_in_app: "Abrir na app", see_map: "Ver o mapa",
    is_appellation: "é uma denominação vitícola",
    located_in_region: "situada na região de",
    in_country: "em",
    sub_region: "sub-região",
    main_grapes: "Castas principais",
    go_further: "Ir mais longe",
    map_of: (name) => `Mapa de ${name}`,
    learn_on_app: (name) => `Aprender ${name} na app geoVINUM`,
    ask_ai: "Perguntar à geoVINUM AI",
    all_appellations_of: (region) => `Todas as denominações de ${region}`,
  },
};

const AppellationExplorePage = () => {
  const { slug } = useParams();
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiFetch("/api/public/appellations?limit=500");
      if (!cancelled && ok) {
        const item = (data?.items || []).find((it) => it.slug === slug);
        setMeta(item || { slug, name: slug, region: "", country: "" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const displayName = useMemo(() => meta?.name || slug, [meta, slug]);

  return (
    <Layout testId={`appellation-${slug}`}>
      <SEO
        title={`${displayName} — geoVINUM`}
        description={meta ? c.seo_desc(displayName, meta.region || meta.country || "") : ""}
        breadcrumbs={[
          { name: c.home, url: "/" },
          { name: c.explorer, url: "/blog" },
          { name: displayName, url: `/explore/${slug}` },
        ]}
        jsonLdList={[
          // `Place` + `containedInPlace` chain lets LLMs read the
          // geographic hierarchy without executing our React tree.
          meta && {
            "@context": "https://schema.org",
            "@type": "Place",
            "@id": `https://geovinum.io/explore/${slug}`,
            name: displayName,
            url: `https://geovinum.io/explore/${slug}`,
            containedInPlace: [
              meta.region && { "@type": "AdministrativeArea", name: meta.region },
              meta.country && { "@type": "Country", name: meta.country },
            ].filter(Boolean),
            additionalType: "https://schema.org/AdministrativeArea",
          },
        ]}
      />
      <ReadingProgressBar targetSelector={`[data-testid="appellation-${slug}"] article`} />

      <section className="bg-[color:var(--gv-mist)] pt-10 pb-8">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Breadcrumb
            items={[
              { to: "/", label: c.home },
              { to: "/blog", label: c.explorer },
              { label: displayName },
            ]}
          />
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)] mt-4 mb-6" data-testid="appellation-back">
            <ArrowLeft size={14} /> {c.back_all}
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">
            {meta?.region && <>{c.label_appellation} · {meta.region}{meta.country && <> · {meta.country}</>}</>}
            {!meta?.region && <>{c.label_appellation}</>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mb-5" data-testid="appellation-title">
            {displayName}
          </h1>
          <div className="flex flex-wrap gap-3">
            <a
              href={`https://app.geovinum.io/appellations/${slug}`}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              data-testid="appellation-open-app"
            >
              {c.open_in_app} <ExternalLink size={14} />
            </a>
            <Link to="/shop" className="btn-secondary" data-testid="appellation-shop">
              <MapIcon size={14} /> {c.see_map}
            </Link>
          </div>
        </div>
      </section>

      <article className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          {/* Phase 2 SEO/GEO — Plain-HTML relations strip so LLM crawlers
              read the geographic + editorial graph without touching JS.
              Placed BEFORE the Atlas blocks so it's the first thing after
              the H1 that a text extractor picks up. */}
          {meta && (
            <nav
              aria-label="Relations"
              className="mb-10 pb-6 border-b border-[color:var(--gv-line)] text-[13.5px] text-[color:var(--gv-ink)]/85 leading-relaxed"
              data-testid="appellation-relations"
            >
              <p>
                <strong>{displayName}</strong> {c.is_appellation}
                {meta.region ? <> {c.located_in_region} <Link to={`/blog?q=${encodeURIComponent(meta.region)}`} className="text-[color:var(--gv-pink)] hover:underline">{meta.region}</Link></> : null}
                {meta.country ? <>, {c.in_country} <Link to={`/blog?q=${encodeURIComponent(meta.country)}`} className="text-[color:var(--gv-pink)] hover:underline">{meta.country}</Link></> : null}
                {meta.subregion ? <> · {c.sub_region} <Link to={`/blog?q=${encodeURIComponent(meta.subregion)}`} className="text-[color:var(--gv-pink)] hover:underline">{meta.subregion}</Link></> : null}
                .
                {Array.isArray(meta.grapes) && meta.grapes.length > 0 && (
                  <> {c.main_grapes} : {meta.grapes.slice(0, 6).map((g, i) => (
                    <span key={g}>
                      {i > 0 ? ", " : ""}
                      <Link to={`/cepage/${encodeURIComponent(String(g).toLowerCase().replace(/\s+/g,'-'))}`} className="text-[color:var(--gv-pink)] hover:underline">{g}</Link>
                    </span>
                  ))}.</>
                )}
              </p>
              <p className="mt-2 text-[color:var(--gv-ink)]/65">
                {c.go_further} —{" "}
                <Link to={`/shop?q=${encodeURIComponent(displayName)}`} className="text-[color:var(--gv-pink)] hover:underline">{c.map_of(displayName)}</Link>
                {" · "}
                <Link to={`/app#academy`} className="text-[color:var(--gv-pink)] hover:underline">{c.learn_on_app(displayName)}</Link>
                {" · "}
                <Link to={`/app#ai`} className="text-[color:var(--gv-pink)] hover:underline">{c.ask_ai}</Link>
                {meta.region ? <> {" · "}<Link to={`/blog?q=${encodeURIComponent(meta.region)}`} className="text-[color:var(--gv-pink)] hover:underline">{c.all_appellations_of(meta.region)}</Link></> : null}
              </p>
            </nav>
          )}

          <AtlasBlocksSection
            appellation={displayName}
            lang={lang}
          />
        </div>
      </article>
    </Layout>
  );
};

export default AppellationExplorePage;
